import React, { useContext, useEffect, useLayoutEffect } from "react"
import { Layout } from "antd"
import Sidebar from "./Sidebar"
import Main from "./Main"
import { TitleProvider } from "../../contexts/TitleContext"

import "./index.less"
import { Route, Router, Switch, useLocation } from "react-router-dom"
import { AuthActions, AuthContext } from "../../contexts/auth/AuthContext"
import history from "../../services/history"
import Auth from "../../pages/auth"
import { RENEW_TOKEN } from "../../rest-api/mutations/renewToken"
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint"
import HeaderHome from "./HeaderHome"
import { ThemeContext } from "../../contexts/ThemeContex"
import { useMutation } from "@tanstack/react-query"
import AICopilot from "../../components/ai/AICopilot"
import PublicInvoice from "../../pages/publicInvoice"
import MobileApp from "../../mobile-app"
import {
  AI_ENABLED_KEY,
  PERMISSIONS_UPDATED_EVENT,
  refreshPermissions,
} from "../../rest-api/queries/me"

/** Mobil ilovada sahifa orqasidagi web tizim uslublari xalaqit bermasin */
function MobileRedirectGuard() {
  useEffect(() => {
    document.body.classList.add("mobile-app-open")
    return () => document.body.classList.remove("mobile-app-open")
  }, [])
  return null
}

/**
 * Telefondan kirgan odamni bir marta mobil ilovaga taklif qiladi.
 *
 * Faqat bir marta: foydalanuvchi "web ko'rinish" ni tanlasa, uni qayta
 * mobil ilovaga tortib ketmaslik kerak. Shuning uchun belgi seans
 * xotirasida saqlanadi.
 */
function MobileAutoRedirect() {
  const location = useLocation()

  useEffect(() => {
    try {
      if (sessionStorage.getItem("mobileRedirectDone") === "1") return
      if (location.pathname !== "/") return
      if (window.innerWidth > 768) return
      sessionStorage.setItem("mobileRedirectDone", "1")
      history.push("/m")
    } catch {
      // sessionStorage yopiq bo'lsa yo'naltirmaymiz
    }
  }, [location.pathname])

  return null
}

function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
    const siteLayout = document.querySelector(".site-layout")
    if (siteLayout) siteLayout.scrollTop = 0
    const content = document.querySelector(".site-layout-content")
    if (content) content.scrollTop = 0
  }, [pathname])

  return null
}

export default function AppLayout() {
  const { authState, authDispatch } = useContext(AuthContext)
  const { themeState } = useContext(ThemeContext)
  const screens = useBreakpoint()
  const { mutate } = useMutation(RENEW_TOKEN, {
    onSuccess: (data) => {
      if (!data?.accessToken) {
        authDispatch({ type: AuthActions.SING_OUT })
        return
      }
      authDispatch({
        type: AuthActions.SIGN_IN,
        payload: {
          accessToken: data?.accessToken,
          refreshToken: data?.refreshToken,
        },
      })
    },
    onError: () => {
      authDispatch({ type: AuthActions.SING_OUT })
    },
  })

  useLayoutEffect(() => {
    if (
      !authState.accessToken ||
      !authState.refreshToken ||
      !authState.isAuthenticated
    ) {
      authDispatch({ type: AuthActions.SING_OUT })
      return
    }

    mutate({
      accessToken: authState.accessToken,
      refreshToken: authState.refreshToken,
    })

    // Bo'limlarga ruxsatlarni backenddan qayta olamiz: admin ruxsatni
    // o'zgartirgan bo'lsa, xodim qayta login qilmasdan ham amal qiladi
    refreshPermissions()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // AI yordamchisi adminning sozlamasiga qarab ko'rinadi. Ruxsatlar
  // yangilanganda (Me qayta o'qilganda) bu ham darhol qayta hisoblanadi,
  // shuning uchun admin o'chirsa xodim qayta kirmasdan ham yo'qoladi.
  const [aiEnabled, setAiEnabled] = React.useState(
    () => localStorage.getItem(AI_ENABLED_KEY) !== "0"
  )
  useEffect(() => {
    const sync = () => setAiEnabled(localStorage.getItem(AI_ENABLED_KEY) !== "0")
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, sync)
    return () => window.removeEventListener(PERMISSIONS_UPDATED_EVENT, sync)
  }, [])

  useEffect(() => {
    const isDark = themeState.currentTheme === "dark"
    if (isDark) {
      document.body.classList.add("dark")
      document.documentElement.classList.add("dark")
      document.getElementById("darkBleck")?.classList.add("dark")
    } else {
      document.body.classList.remove("dark")
      document.documentElement.classList.remove("dark")
      document.getElementById("darkBleck")?.classList.remove("dark")
    }
    document.getElementById("root")?.classList.add("apply-font")
  }, [themeState.currentTheme])

  return (
    <div className="WrapperProject">
      <Router history={history}>
        <ScrollToTop />
        <MobileAutoRedirect />
        <Switch>
          {/*
            Mijozga yuboriladigan invoys havolasi. Tizimga kirish talab
            qilinmaydi, shuning uchun u autentifikatsiya tekshiruvidan
            oldin turadi.
          */}
          <Route path="/invoice/:token">
            <PublicInvoice />
          </Route>
          {/*
            Mobil ilova. O'z qobig'i bor: chap panel, yuqori panel va
            boshqa og'ir qismlar yuklanmaydi, shuning uchun telefonda
            tez ochiladi. Tizimga kirmagan odam baribir kirish
            sahifasiga tushadi.
          */}
          <Route path="/m">
            {authState.isAuthenticated ? (
              <>
                <MobileRedirectGuard />
                <MobileApp />
              </>
            ) : (
              <Auth />
            )}
          </Route>
          <Route>
        {authState.isAuthenticated ? (
          <TitleProvider>
            <Layout style={{ minHeight: "100vh" }}>
              <Sidebar />
              <Layout
                className={`site-layout ${
                  !screens.lg ? "site-layout-mobile" : ""
                }`}
              >
                <HeaderHome />
                <div className="site-layout-content">
                  <Main />
                </div>
              </Layout>
            </Layout>
            {aiEnabled && <AICopilot />}
          </TitleProvider>
        ) : (
          <Auth />
        )}
          </Route>
        </Switch>
      </Router>
    </div>
  )
}
